// src/App.jsx
import React, { useCallback, useEffect, useState, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Wallet,
  ArrowLeft,
  BarChart3,
  Calculator,
  List,
  FileText,
  ArrowDownCircle,
  ArrowUpCircle,
  PieChart,
  Download,
  Copy,
  TrendingUp,
  Landmark,
  LineChart,
  FileSpreadsheet,
} from "lucide-react";

import { useTransactions } from "./hooks/useTransactions";

import TransactionForm from "./components/TransactionForm";
import KPICards from "./components/KPICards";
import AIInsights from "./components/AIInsights";
import {
  deleteProject,
  getProjectById,
  saveProjectTransactionSettings,
} from "./lib/db";

import {
  DEFAULT_CATEGORIES,
  DEFAULT_TRANSACTION_TYPES,
  formatCurrency,
  getTransactionImpactSign,
  normalizeTransactionTypeKey,
  resolveTransactionTypeConfig,
} from "./lib/constants";

const ROICalculator = React.lazy(() => import("./components/ROICalculator"));

export default function App({ user, logout }) {
  const { workspaceId } = useParams();
  const navigate = useNavigate();

  const [currentProject, setCurrentProject] = useState(null);
  const [activeTab, setActiveTab] = useState("dashboard");
  const [reportType, setReportType] = useState("balance-sheet");
  const [editingTx, setEditingTx] = useState(null);
  const [filterType, setFilterType] = useState("all");
  const [filterCategory, setFilterCategory] = useState("all");
  const [ledgerSearch, setLedgerSearch] = useState("");
  const [ledgerSort, setLedgerSort] = useState({
    column: "date",
    direction: "desc",
  });
  const [ledgerPage, setLedgerPage] = useState(1);
  const [ledgerRowsPerPage, setLedgerRowsPerPage] = useState("10");
  const [selectedLedgerIds, setSelectedLedgerIds] = useState([]);
  const [workspaceLoading, setWorkspaceLoading] = useState(true);
  const [workspaceError, setWorkspaceError] = useState("");
  const [isDeletingWorkspace, setIsDeletingWorkspace] = useState(false);
  const [transactionTypes, setTransactionTypes] = useState(
    DEFAULT_TRANSACTION_TYPES,
  );
  const [categories, setCategories] = useState(DEFAULT_CATEGORIES);
  const [bulkEditOpen, setBulkEditOpen] = useState(false);
  const [bulkEditForm, setBulkEditForm] = useState({
    date: "",
    type: "",
    category: "",
    amount: "",
    payee: "",
    notes: "",
  });

  const { transactions, add, update, remove } = useTransactions(
    user?.uid,
    workspaceId,
  );

  const getTypeKey = useCallback(
    (rawType) => normalizeTransactionTypeKey(rawType, transactionTypes),
    [transactionTypes],
  );
  const getTypeConfig = useCallback(
    (rawType) => resolveTransactionTypeConfig(rawType, transactionTypes),
    [transactionTypes],
  );
  const getAmountImpact = useCallback(
    (tx) =>
      getTransactionImpactSign(tx.type, transactionTypes) *
      Math.abs(Number(tx.amount) || 0),
    [transactionTypes],
  );

  const getTypeKeyForSave = useCallback(
    (rawType) => {
      const raw = String(rawType || "").trim().toLowerCase();
      const normalized = getTypeKey(raw);
      if (
        normalized === "expense" &&
        raw &&
        raw !== "expense" &&
        !transactionTypes[raw]
      ) {
        return raw;
      }
      return normalized;
    },
    [getTypeKey, transactionTypes],
  );

  const persistTransactionSettings = useCallback(
    async (nextTypes, nextCategories) => {
      if (!user?.uid || !workspaceId) return;
      await saveProjectTransactionSettings(user.uid, workspaceId, {
        types: nextTypes,
        categories: nextCategories,
      });
    },
    [user?.uid, workspaceId],
  );

  const upsertTransactionType = async (key, config) => {
    const normalizedKey = normalizeTransactionTypeKey(key, {
      ...transactionTypes,
      [key]: config,
    });
    const nextTypes = {
      ...transactionTypes,
      [normalizedKey]: {
        label: config.label,
        type: config.type || "outflow",
      },
    };
    const nextCategories = {
      ...categories,
      [normalizedKey]: categories[normalizedKey] || ["General", "Other"],
    };
    setTransactionTypes(nextTypes);
    setCategories(nextCategories);
    await persistTransactionSettings(nextTypes, nextCategories);
  };

  const addCategoryToType = async (typeKey, categoryName) => {
    const rawType = String(typeKey || "").trim().toLowerCase();
    let normalizedType = normalizeTransactionTypeKey(rawType, transactionTypes);
    if (
      normalizedType === "expense" &&
      rawType &&
      rawType !== "expense" &&
      !transactionTypes[rawType]
    ) {
      normalizedType = rawType;
    }
    const value = String(categoryName || "").trim();
    if (!value) return;
    const existing = categories[normalizedType] || [];
    if (existing.includes(value)) return;
    const nextCategories = {
      ...categories,
      [normalizedType]: [...existing, value],
    };
    setCategories(nextCategories);
    await persistTransactionSettings(transactionTypes, nextCategories);
  };

  const saveTransaction = (tx) => {
    const normalizedType = getTypeKeyForSave(tx.type);
    return add({
      ...tx,
      type: normalizedType,
      category: tx.category || categories[normalizedType]?.[0] || "General",
      amount: Math.abs(Number(tx.amount) || 0),
    });
  };

  const updateTransactionRecord = (id, tx) => {
    const payload = { ...tx };

    if ("type" in tx && tx.type) {
      const normalizedType = getTypeKeyForSave(tx.type);
      payload.type = normalizedType;
      if (!payload.category) {
        payload.category = categories[normalizedType]?.[0] || "General";
      }
    }

    if ("amount" in tx) {
      const parsedAmount = Number(tx.amount);
      if (Number.isFinite(parsedAmount)) {
        payload.amount = Math.abs(parsedAmount);
      } else {
        delete payload.amount;
      }
    }

    return update(id, payload);
  };

  useEffect(() => {
    let cancelled = false;
    if (!user?.uid || !workspaceId) return;
    setWorkspaceLoading(true);
    setWorkspaceError("");
    setCurrentProject(null);
    setEditingTx(null);
    setActiveTab("dashboard");

    (async () => {
      try {
        const project = await getProjectById(user.uid, workspaceId);
        if (cancelled) return;
        if (!project) {
          setWorkspaceError("Workspace not found.");
          setTransactionTypes(DEFAULT_TRANSACTION_TYPES);
          setCategories(DEFAULT_CATEGORIES);
        } else {
          const savedTypes = project.transactionSettings?.types;
          const savedCategories = project.transactionSettings?.categories;
          const normalizedSavedTypes =
            savedTypes && typeof savedTypes === "object"
              ? Object.entries(savedTypes).reduce((acc, [rawKey, config]) => {
                  const normalizedKey = normalizeTransactionTypeKey(rawKey, {
                    ...DEFAULT_TRANSACTION_TYPES,
                    ...savedTypes,
                  });
                  if (!acc[normalizedKey]) acc[normalizedKey] = config;
                  return acc;
                }, {})
              : {};
          const normalizedSavedCategories =
            savedCategories && typeof savedCategories === "object"
              ? Object.entries(savedCategories).reduce((acc, [rawKey, list]) => {
                  const normalizedKey = normalizeTransactionTypeKey(rawKey, {
                    ...DEFAULT_TRANSACTION_TYPES,
                    ...savedTypes,
                  });
                  const current = acc[normalizedKey] || [];
                  const incoming = Array.isArray(list) ? list : [];
                  acc[normalizedKey] = Array.from(new Set([...current, ...incoming]));
                  return acc;
                }, {})
              : {};
          setTransactionTypes(
            savedTypes && typeof savedTypes === "object"
              ? { ...DEFAULT_TRANSACTION_TYPES, ...normalizedSavedTypes }
              : DEFAULT_TRANSACTION_TYPES,
          );
          setCategories(
            savedCategories && typeof savedCategories === "object"
              ? { ...DEFAULT_CATEGORIES, ...normalizedSavedCategories }
              : DEFAULT_CATEGORIES,
          );
        }
        setCurrentProject(project);
      } catch {
        if (!cancelled) setWorkspaceError("Failed to load workspace.");
      } finally {
        if (!cancelled) setWorkspaceLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user?.uid, workspaceId]);

  const handleDeleteWorkspace = async () => {
    if (!currentProject?.id || !user?.uid || isDeletingWorkspace) return;
    const shouldDelete = window.confirm(
      `Delete workspace "${currentProject.name}"? This action cannot be undone.`,
    );
    if (!shouldDelete) return;

    try {
      setIsDeletingWorkspace(true);
      await deleteProject(user.uid, currentProject.id);
      navigate("/");
    } finally {
      setIsDeletingWorkspace(false);
    }
  };

  const { totalInflows, totalOutflows, balance, expensesByCategory } =
    useMemo(() => {
      let inflows = 0,
        outflows = 0;
      const byCategory = {};
      transactions.forEach((t) => {
        const amt = Math.abs(Number(t.amount) || 0);
        const typeConfig = getTypeConfig(t.type);
        if (typeConfig.type === "inflow") {
          inflows += amt;
        } else {
          outflows += amt;
          byCategory[t.category] = (byCategory[t.category] || 0) + amt;
        }
      });
      return {
        totalInflows: inflows,
        totalOutflows: outflows,
        balance: inflows - outflows,
        expensesByCategory: Object.entries(byCategory).sort(
          (a, b) => b[1] - a[1],
        ),
      };
    }, [transactions, getTypeConfig]);

  const { filtered, filterCats } = useMemo(() => {
    let result = [...transactions];
    if (filterType !== "all") {
      result = result.filter((t) => {
        if (filterType === "inflow") return getTypeConfig(t.type).type === "inflow";
        if (filterType === "outflow") return getTypeConfig(t.type).type !== "inflow";
        return getTypeKey(t.type) === filterType;
      });
    }
    const cats = new Set(result.map((t) => t.category));
    if (filterCategory !== "all")
      result = result.filter((t) => t.category === filterCategory);

    const searchText = ledgerSearch.trim().toLowerCase();
    if (searchText) {
      result = result.filter((t) => {
        const typeLabel = getTypeConfig(t.type).label || t.type;
        const absoluteAmount = Math.abs(Number(t.amount) || 0);
        const signedAmount = getAmountImpact(t);
        const searchFields = [
          t.date,
          typeLabel,
          t.category,
          t.payee,
          t.notes || "",
          String(absoluteAmount),
          String(signedAmount),
          formatCurrency(absoluteAmount),
        ];
        return searchFields.some((value) =>
          String(value || "").toLowerCase().includes(searchText),
        );
      });
    }

    result.sort((a, b) => {
      let left = "";
      let right = "";

      if (ledgerSort.column === "date") {
        left = new Date(a.date).getTime();
        right = new Date(b.date).getTime();
      } else if (ledgerSort.column === "type") {
        left = (getTypeConfig(a.type).label || a.type || "").toLowerCase();
        right = (getTypeConfig(b.type).label || b.type || "").toLowerCase();
      } else if (ledgerSort.column === "category") {
        left = String(a.category || "").toLowerCase();
        right = String(b.category || "").toLowerCase();
      } else if (ledgerSort.column === "payee") {
        left = String(a.payee || "").toLowerCase();
        right = String(b.payee || "").toLowerCase();
      } else if (ledgerSort.column === "notes") {
        left = String(a.notes || "").toLowerCase();
        right = String(b.notes || "").toLowerCase();
      } else if (ledgerSort.column === "amount") {
        left = getAmountImpact(a);
        right = getAmountImpact(b);
      }

      if (left < right) return ledgerSort.direction === "asc" ? -1 : 1;
      if (left > right) return ledgerSort.direction === "asc" ? 1 : -1;
      return 0;
    });
    return { filtered: result, filterCats: Array.from(cats).sort() };
  }, [
    transactions,
    filterType,
    filterCategory,
    ledgerSearch,
    ledgerSort,
    getTypeConfig,
    getTypeKey,
    getAmountImpact,
  ]);

  const handleLedgerSort = (column) => {
    setLedgerSort((prev) => {
      if (prev.column === column) {
        return {
          column,
          direction: prev.direction === "asc" ? "desc" : "asc",
        };
      }
      return { column, direction: "asc" };
    });
  };

  const totalLedgerRows = filtered.length;
  const isShowAllRows = ledgerRowsPerPage === "all";
  const totalLedgerPages = isShowAllRows
    ? 1
    : Math.max(1, Math.ceil(totalLedgerRows / Number(ledgerRowsPerPage)));
  const currentLedgerPage = Math.min(ledgerPage, totalLedgerPages);
  const paginatedLedgerRows = useMemo(() => {
    if (isShowAllRows) return filtered;
    const pageSize = Number(ledgerRowsPerPage);
    const start = (currentLedgerPage - 1) * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, isShowAllRows, ledgerRowsPerPage, currentLedgerPage]);
  const visibleLedgerIds = useMemo(
    () => paginatedLedgerRows.map((t) => t.id),
    [paginatedLedgerRows],
  );
  const bulkCategoryOptions = useMemo(() => {
    if (bulkEditForm.type) {
      return categories[getTypeKey(bulkEditForm.type)] || [];
    }
    return Array.from(
      new Set(Object.values(categories).flat().filter(Boolean)),
    ).sort((a, b) => a.localeCompare(b));
  }, [bulkEditForm.type, categories, getTypeKey]);
  const allVisibleLedgerSelected =
    !!visibleLedgerIds.length &&
    visibleLedgerIds.every((id) => selectedLedgerIds.includes(id));

  useEffect(() => {
    setSelectedLedgerIds((prev) =>
      prev.filter((id) => transactions.some((t) => t.id === id)),
    );
  }, [transactions]);

  useEffect(() => {
    setLedgerPage(1);
  }, [filterType, filterCategory, ledgerSearch, ledgerSort, transactions.length]);

  useEffect(() => {
    if (ledgerPage > totalLedgerPages) {
      setLedgerPage(totalLedgerPages);
    }
  }, [ledgerPage, totalLedgerPages]);

  useEffect(() => {
    if (!transactions.length) return;
    let hasChanges = false;
    const nextTypes = { ...transactionTypes };
    const nextCategories = { ...categories };

    transactions.forEach((t) => {
      const typeKey = getTypeKey(t.type);
      if (!nextTypes[typeKey]) {
        nextTypes[typeKey] = { label: typeKey, type: "outflow" };
        hasChanges = true;
      }
      const category = String(t.category || "").trim();
      if (!category) return;
      const existing = nextCategories[typeKey] || [];
      if (!existing.includes(category)) {
        nextCategories[typeKey] = [...existing, category];
        hasChanges = true;
      }
    });

    if (!hasChanges) return;
    setTransactionTypes(nextTypes);
    setCategories(nextCategories);
    void persistTransactionSettings(nextTypes, nextCategories);
  }, [
    transactions,
    transactionTypes,
    categories,
    getTypeKey,
    persistTransactionSettings,
  ]);

  const toggleLedgerSelection = (id) => {
    setSelectedLedgerIds((prev) =>
      prev.includes(id)
        ? prev.filter((selectedId) => selectedId !== id)
        : [...prev, id],
    );
  };

  const toggleSelectAllVisibleLedger = () => {
    setSelectedLedgerIds((prev) => {
      if (allVisibleLedgerSelected) {
        return prev.filter((id) => !visibleLedgerIds.includes(id));
      }
      const merged = new Set([...prev, ...visibleLedgerIds]);
      return Array.from(merged);
    });
  };

  const clearLedgerSelection = () => setSelectedLedgerIds([]);

  const handleDeleteSelectedLedger = async () => {
    if (!selectedLedgerIds.length) return;
    const shouldDelete = window.confirm(
      `Delete ${selectedLedgerIds.length} selected transaction(s)? This action cannot be undone.`,
    );
    if (!shouldDelete) return;

    await Promise.all(selectedLedgerIds.map((id) => remove(id)));
    setSelectedLedgerIds([]);
  };

  const openBulkEdit = () => {
    if (!selectedLedgerIds.length) return;
    setBulkEditForm({
      date: "",
      type: "",
      category: "",
      amount: "",
      payee: "",
      notes: "",
    });
    setBulkEditOpen(true);
  };

  const applyBulkEdit = async () => {
    if (!selectedLedgerIds.length) return;

    const normalizedType = bulkEditForm.type
      ? getTypeKeyForSave(bulkEditForm.type)
      : "";
    const patch = {};
    if (bulkEditForm.date) patch.date = bulkEditForm.date;
    if (normalizedType) patch.type = normalizedType;
    if (bulkEditForm.category) patch.category = bulkEditForm.category;
    if (bulkEditForm.amount !== "") {
      const parsedAmount = Number(bulkEditForm.amount);
      if (Number.isFinite(parsedAmount)) patch.amount = Math.abs(parsedAmount);
    }
    if (bulkEditForm.payee.trim()) patch.payee = bulkEditForm.payee.trim();
    if (bulkEditForm.notes.trim()) patch.notes = bulkEditForm.notes.trim();

    if (
      normalizedType &&
      !patch.category &&
      (categories[normalizedType] || []).length
    ) {
      patch.category = categories[normalizedType][0];
    }

    if (!Object.keys(patch).length) return;
    await Promise.all(
      selectedLedgerIds.map((id) => updateTransactionRecord(id, patch)),
    );
    setBulkEditOpen(false);
  };

  const duplicateTransaction = async (tx) => {
    const duplicated = {
      date: new Date().toISOString().split("T")[0],
      type: getTypeKeyForSave(tx.type),
      category: tx.category,
      payee: tx.payee,
      amount: Math.abs(Number(tx.amount) || 0),
      notes: tx.notes || "",
    };
    const duplicatedId = await saveTransaction(duplicated);
    setEditingTx({ ...duplicated, id: duplicatedId });
    setActiveTab("dashboard");
  };

  const exportCSV = () => {
    const data = filtered.length ? filtered : transactions;
    if (!data.length) return;
    const rows = data.map((t) => {
      const typeConfig = getTypeConfig(t.type);
      const impactAmount = getAmountImpact(t);
      return [
        t.date,
        `"${typeConfig.label || t.type}"`,
        `"${t.category}"`,
        `"${t.payee.replace(/"/g, '""')}"`,
        `"${(t.notes || "").replace(/"/g, '""')}"`,
        impactAmount,
      ].join(",");
    });
    const csv = ["Date,Type,Category,Payee,Notes,Amount", ...rows].join("\n");
    const link = document.createElement("a");
    link.href = URL.createObjectURL(
      new Blob([csv], { type: "text/csv;charset=utf-8;" }),
    );
    link.download = `${currentProject.name.replace(/\s+/g, "_")}_Ledger.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const formatAcc = (v) =>
    v < 0 ? `(${formatCurrency(Math.abs(v))})` : formatCurrency(v);

  // Derived Data: Financial Statements
  const financialData = useMemo(() => {
    let revenue = 0,
      expenses = 0,
      liabilities = 0,
      equity = 0;
    const expB = {},
      liabB = {},
      eqB = {},
      revB = {};

    transactions.forEach((t) => {
      const amt = Math.abs(Number(t.amount) || 0);
      const typeConfig = getTypeConfig(t.type);
      const catLower = t.category.toLowerCase();

      if (typeConfig.type === "inflow") {
        if (catLower.match(/revenue|sales|income/)) {
          revenue += amt;
          revB[t.category] = (revB[t.category] || 0) + amt;
        } else if (catLower.match(/equity|grant|capital|owner/)) {
          equity += amt;
          eqB[t.category] = (eqB[t.category] || 0) + amt;
        } else {
          liabilities += amt;
          liabB[t.category] = (liabB[t.category] || 0) + amt;
        }
      } else if (typeConfig.type === "liability") {
        liabilities += amt;
        liabB[t.category] = (liabB[t.category] || 0) + amt;
      } else {
        expenses += amt;
        expB[t.category] = (expB[t.category] || 0) + amt;
      }
    });

    const netIncome = revenue - expenses;
    return {
      revenue,
      expenses,
      liabilities,
      equity,
      netIncome,
      totalAssets: balance,
      totalLiabilitiesAndEquity: liabilities + equity + netIncome,
      expenseBreakdown: Object.entries(expB).sort((a, b) => b[1] - a[1]),
      liabilityBreakdown: Object.entries(liabB).sort((a, b) => b[1] - a[1]),
      equityBreakdown: Object.entries(eqB).sort((a, b) => b[1] - a[1]),
      revenueBreakdown: Object.entries(revB).sort((a, b) => b[1] - a[1]),
      cashFromOperations: revenue - expenses,
      cashFromFinancing: equity - liabilities,
    };
  }, [transactions, balance, getTypeConfig]);

  // ── Loading ───────────────────────────────────────────
  if (workspaceLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 text-slate-500 font-medium">
        Loading workspace...
      </div>
    );
  }

  if (workspaceError) {
    return (
      <div className="min-h-screen flex flex-col gap-3 items-center justify-center bg-slate-50 p-6">
        <p className="text-sm font-semibold text-rose-700">{workspaceError}</p>
        <button
          onClick={() => navigate("/")}
          className="px-4 py-2 rounded-lg bg-slate-900 text-white text-sm font-semibold"
        >
          Back to Workspaces
        </button>
      </div>
    );
  }

  if (!currentProject) {
    return null;
  }

  // ── Main app ──────────────────────────────────────────
  const tabs = [
    { id: "dashboard", label: "Dashboard" },
    { id: "ledger", label: "Detailed Ledger" },
    {
      id: "reports",
      label: "Reports",
      icon: <BarChart3 className="w-4 h-4 mr-1.5 inline" />,
    },
    {
      id: "roi",
      label: "ROI Calculator",
      icon: <Calculator className="w-4 h-4 mr-1.5 inline" />,
    },
  ];

  return (
    <div className="min-h-screen bg-slate-50 text-slate-800 font-sans p-4 md:p-6 lg:p-8">
      <div className="max-w-7xl mx-auto space-y-6">
        <header className="bg-white p-5 rounded-2xl shadow-sm border border-slate-200 flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
          <div className="flex flex-col items-start gap-3">
            <button
              onClick={() => navigate("/")}
              className="flex items-center gap-1.5 text-xs font-bold text-slate-500 hover:text-indigo-600 bg-slate-100 hover:bg-indigo-50 px-3 py-1.5 rounded-full transition-colors"
            >
              <ArrowLeft className="w-3.5 h-3.5" /> All Projects
            </button>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-indigo-50 rounded-xl">
                <Wallet className="w-6 h-6 text-indigo-600" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-slate-900">
                  {currentProject.name} Tracker
                </h1>
                <p className="text-slate-500 text-xs mt-0.5 font-medium">
                  Manage disbursements, expenses, and cash flow
                </p>
              </div>
            </div>
          </div>

          {/* ← ADDED: Sign Out button in nav */}
          <div className="flex bg-slate-100/80 p-1.5 rounded-xl overflow-x-auto w-full lg:w-auto flex-nowrap shrink-0 gap-1">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all whitespace-nowrap ${
                  activeTab === tab.id
                    ? "bg-white text-indigo-700 shadow-sm border border-slate-200/50"
                    : "text-slate-500 hover:text-slate-800 hover:bg-slate-200/50"
                }`}
              >
                {tab.icon}
                {tab.label}
              </button>
            ))}
            <button
              onClick={handleDeleteWorkspace}
              disabled={isDeletingWorkspace}
              className="ml-2 px-4 py-2 rounded-lg text-sm font-semibold text-rose-500 hover:text-rose-700 hover:bg-rose-50 transition-all whitespace-nowrap disabled:opacity-50"
            >
              {isDeletingWorkspace ? "Deleting..." : "Delete Workspace"}
            </button>
            <button
              onClick={logout}
              className="px-4 py-2 rounded-lg text-sm font-semibold text-slate-400 hover:text-rose-600 hover:bg-rose-50 transition-all whitespace-nowrap"
            >
              Sign Out
            </button>
          </div>
        </header>

        {/* DASHBOARD */}
        {activeTab === "dashboard" && (
          <div className="space-y-6">
            <KPICards
              totalInflows={totalInflows}
              totalOutflows={totalOutflows}
              balance={balance}
            />
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-1">
                <TransactionForm
                  onAdd={saveTransaction}
                  onUpdate={updateTransactionRecord}
                  editingTx={editingTx}
                  onCancelEdit={() => setEditingTx(null)}
                  transactionTypes={transactionTypes}
                  categories={categories}
                  onCreateType={upsertTransactionType}
                  onCreateCategory={addCategoryToType}
                />
              </div>
              <div className="lg:col-span-2 space-y-6">
                <AIInsights
                  totalInflows={totalInflows}
                  totalOutflows={totalOutflows}
                  balance={balance}
                  expensesByCategory={expensesByCategory}
                />
                <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100">
                  <h2 className="text-base font-bold text-slate-800 mb-6 flex items-center gap-2">
                    <PieChart className="w-5 h-5 text-indigo-500" /> Outflow
                    Distribution
                  </h2>
                  {!expensesByCategory.length ? (
                    <div className="text-center py-8 text-slate-400 text-sm bg-slate-50 rounded-xl border border-dashed border-slate-200">
                      No outflows recorded yet.
                    </div>
                  ) : (
                    <div className="space-y-5">
                      {expensesByCategory.map(([cat, amt]) => {
                        const pct =
                          totalOutflows > 0
                            ? ((amt / totalOutflows) * 100).toFixed(1)
                            : 0;
                        return (
                          <div key={cat} className="space-y-1.5">
                            <div className="flex justify-between text-sm">
                              <span className="font-bold text-slate-700">
                                {cat}
                              </span>
                              <span className="text-slate-500 font-medium">
                                {formatCurrency(amt)}{" "}
                                <span className="text-xs text-slate-400">
                                  ({pct}%)
                                </span>
                              </span>
                            </div>
                            <div className="w-full bg-slate-100 rounded-full h-2">
                              <div
                                className="bg-indigo-500 h-2 rounded-full"
                                style={{ width: `${pct}%` }}
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
                <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100">
                  <div className="flex justify-between items-center mb-5">
                    <h2 className="text-base font-bold text-slate-800 flex items-center gap-2">
                      <List className="w-5 h-5 text-indigo-500" /> Recent
                      Activity
                    </h2>
                    <button
                      onClick={() => setActiveTab("ledger")}
                      className="text-xs text-indigo-600 hover:text-indigo-800 font-bold"
                    >
                      View All →
                    </button>
                  </div>
                  <div className="divide-y divide-slate-100">
                    {transactions.slice(0, 4).map((t) => {
                      const typeConfig = getTypeConfig(t.type);
                      const isInflow = typeConfig.type === "inflow";
                      return (
                        <div
                          key={t.id}
                          className="py-3.5 flex justify-between items-center group"
                        >
                          <div className="flex items-center gap-3.5">
                            <div
                              className={`p-2 rounded-xl border ${isInflow ? "bg-indigo-50 text-indigo-600 border-indigo-100" : "bg-slate-50 text-slate-500 border-slate-200"}`}
                            >
                              {isInflow ? (
                                <ArrowDownCircle className="w-4 h-4" />
                              ) : (
                                <ArrowUpCircle className="w-4 h-4" />
                              )}
                            </div>
                            <div>
                              <p className="text-sm font-bold text-slate-800">
                                {t.payee}
                              </p>
                              <p className="text-xs text-slate-500">
                                {t.category} · {t.date}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <button
                              onClick={() => {
                                const normalizedType = getTypeKey(t.type);
                                setEditingTx({
                                  ...t,
                                  type: normalizedType,
                                  amount: Math.abs(Number(t.amount) || 0),
                                });
                                setActiveTab("dashboard");
                              }}
                              className="opacity-0 group-hover:opacity-100 text-xs text-indigo-500 font-bold"
                            >
                              Edit
                            </button>
                            <span
                              className={`text-sm font-bold font-mono ${isInflow ? "text-indigo-600" : "text-slate-800"}`}
                            >
                              {getAmountImpact(t) >= 0 ? "+" : "-"}
                              {formatCurrency(Math.abs(Number(t.amount) || 0))}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                    {!transactions.length && (
                      <div className="text-center py-6 text-slate-400 text-sm bg-slate-50 rounded-xl border border-dashed border-slate-200">
                        No transactions yet.
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* LEDGER */}
        {activeTab === "ledger" && (
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
            <div className="p-6 border-b border-slate-100 flex justify-between items-center">
              <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                <FileText className="w-5 h-5 text-indigo-500" /> Complete
                Transaction Ledger
              </h2>
              <button
                onClick={exportCSV}
                disabled={!transactions.length}
                className="flex items-center gap-2 px-4 py-2 bg-white text-slate-700 border border-slate-200 rounded-lg text-sm font-bold hover:bg-slate-50 disabled:opacity-50"
              >
                <Download className="w-4 h-4" /> Export to Excel
              </button>
            </div>
            <div className="bg-slate-50 p-5 border-b flex flex-wrap gap-4 items-center justify-between">
              <div className="flex flex-wrap gap-4">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-bold text-slate-500 uppercase">
                    Type:
                  </span>
                  <select
                    value={filterType}
                    onChange={(e) => {
                      setFilterType(e.target.value);
                      setFilterCategory("all");
                    }}
                    className="p-2 text-sm border border-slate-200 rounded-lg bg-white outline-none"
                  >
                    <option value="all">All Types</option>
                    <option value="inflow">All Inflows (+)</option>
                    <option value="outflow">All Outflows (-)</option>
                    {Object.entries(transactionTypes).map(([k, c]) => (
                      <option key={k} value={k}>
                        {c.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs font-bold text-slate-500 uppercase">
                    Category:
                  </span>
                  <select
                    value={filterCategory}
                    onChange={(e) => setFilterCategory(e.target.value)}
                    className="p-2 text-sm border border-slate-200 rounded-lg bg-white outline-none"
                  >
                    <option value="all">All Categories</option>
                    {filterCats.map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <input
                  type="text"
                  value={ledgerSearch}
                  onChange={(e) => setLedgerSearch(e.target.value)}
                  placeholder="Search date, type, category, payee, notes, amount..."
                  className="w-full sm:w-[340px] p-2 text-sm border border-slate-200 rounded-lg bg-white outline-none"
                />
                <button
                  onClick={toggleSelectAllVisibleLedger}
                  disabled={!visibleLedgerIds.length}
                  className="px-3 py-2 text-xs font-bold rounded-lg bg-white border border-slate-200 text-slate-700 hover:bg-slate-100 disabled:opacity-50"
                >
                  {allVisibleLedgerSelected ? "Unselect Visible" : "Select Visible"}
                </button>
                <button
                  onClick={clearLedgerSelection}
                  disabled={!selectedLedgerIds.length}
                  className="px-3 py-2 text-xs font-bold rounded-lg bg-slate-100 text-slate-700 hover:bg-slate-200 disabled:opacity-50"
                >
                  Clear Selection
                </button>
                <button
                  onClick={openBulkEdit}
                  disabled={!selectedLedgerIds.length}
                  className="px-3 py-2 text-xs font-bold rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
                >
                  Bulk Edit ({selectedLedgerIds.length})
                </button>
                <button
                  onClick={handleDeleteSelectedLedger}
                  disabled={!selectedLedgerIds.length}
                  className="px-3 py-2 text-xs font-bold rounded-lg bg-rose-600 text-white hover:bg-rose-700 disabled:opacity-50"
                >
                  Delete Selected ({selectedLedgerIds.length})
                </button>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="bg-white text-slate-500 font-bold text-xs uppercase tracking-wider border-b border-slate-200">
                  <tr>
                    <th className="px-4 py-4 text-center">
                      <input
                        type="checkbox"
                        checked={allVisibleLedgerSelected}
                        onChange={toggleSelectAllVisibleLedger}
                        className="rounded border-slate-300"
                        aria-label="Select all visible transactions"
                      />
                    </th>
                    {[
                      { key: "date", label: "Date" },
                      { key: "type", label: "Type" },
                      { key: "category", label: "Category" },
                      { key: "payee", label: "Payee / Vendor" },
                      { key: "notes", label: "Notes" },
                      { key: "amount", label: "Amount", align: "right" },
                    ].map((column) => (
                      <th
                        key={column.key}
                        className={`px-6 py-4 ${column.align === "right" ? "text-right" : ""}`}
                      >
                        <button
                          type="button"
                          onClick={() => handleLedgerSort(column.key)}
                          className={`inline-flex items-center gap-1 hover:text-slate-700 ${column.align === "right" ? "justify-end w-full" : ""}`}
                        >
                          <span>{column.label}</span>
                          <span
                            className={`text-[15px] leading-none ${ledgerSort.column === column.key ? "text-slate-700" : "text-slate-300"}`}
                            aria-hidden="true"
                          >
                            {ledgerSort.column === column.key &&
                            ledgerSort.direction === "asc"
                              ? "∧"
                              : "∨"}
                          </span>
                        </button>
                      </th>
                    ))}
                    <th className="px-6 py-4 text-right">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {paginatedLedgerRows.map((t) => {
                    const typeConfig = getTypeConfig(t.type);
                    const isInflow = typeConfig.type === "inflow";
                    return (
                      <tr key={t.id} className="hover:bg-slate-50">
                        <td className="px-4 py-4 text-center">
                          <input
                            type="checkbox"
                            checked={selectedLedgerIds.includes(t.id)}
                            onChange={() => toggleLedgerSelection(t.id)}
                            className="rounded border-slate-300"
                            aria-label={`Select transaction ${t.payee}`}
                          />
                        </td>
                        <td className="px-6 py-4 text-slate-600 font-medium whitespace-nowrap">
                          {t.date}
                        </td>
                        <td className="px-6 py-4">
                          <span
                            className={`inline-flex px-2 py-1 rounded-md text-[11px] font-bold uppercase ${isInflow ? "bg-indigo-50 text-indigo-700" : "bg-slate-100 text-slate-600"}`}
                          >
                            {typeConfig.label || t.type}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-slate-800">
                          {t.category}
                        </td>
                        <td className="px-6 py-4 font-bold text-slate-900">
                          {t.payee}
                        </td>
                        <td className="px-6 py-4 text-slate-500 truncate max-w-[200px]">
                          {t.notes}
                        </td>
                        <td
                          className={`px-6 py-4 text-right font-bold font-mono whitespace-nowrap ${isInflow ? "text-indigo-600" : "text-slate-900"}`}
                        >
                          {getAmountImpact(t) >= 0 ? "+" : "-"}
                          {formatCurrency(Math.abs(Number(t.amount) || 0))}
                        </td>
                        <td className="px-6 py-4 text-right whitespace-nowrap">
                          <button
                            onClick={() => {
                              const normalizedType = getTypeKey(t.type);
                              setEditingTx({
                                ...t,
                                type: normalizedType,
                                amount: Math.abs(Number(t.amount) || 0),
                              });
                              setActiveTab("dashboard");
                            }}
                            className="text-indigo-600 hover:text-indigo-800 text-xs font-bold mr-4"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => duplicateTransaction(t)}
                            className="text-slate-600 hover:text-slate-900 text-xs font-bold mr-4 inline-flex items-center gap-1"
                          >
                            <Copy className="w-3 h-3" /> Duplicate
                          </button>
                          <button
                            onClick={() => {
                              remove(t.id);
                              setSelectedLedgerIds((prev) =>
                                prev.filter((id) => id !== t.id),
                              );
                            }}
                            className="text-rose-500 hover:text-rose-700 text-xs font-bold"
                          >
                            Del
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                  {!filtered.length && (
                    <tr>
                      <td
                        colSpan={8}
                        className="px-6 py-12 text-center text-slate-500 bg-slate-50"
                      >
                        No transactions match the criteria.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            {!!filtered.length && (
              <div className="px-6 py-4 border-t border-slate-200 bg-white flex flex-wrap items-center justify-between gap-3">
                <div className="text-xs text-slate-500 font-medium">
                  Showing{" "}
                  {isShowAllRows
                    ? totalLedgerRows
                    : Math.min(
                        totalLedgerRows,
                        (currentLedgerPage - 1) * Number(ledgerRowsPerPage) +
                          paginatedLedgerRows.length,
                      )}{" "}
                  of {totalLedgerRows} row(s)
                </div>
                <div className="flex items-center gap-2">
                  <label className="text-xs font-bold text-slate-500 uppercase">
                    Rows:
                  </label>
                  <select
                    value={ledgerRowsPerPage}
                    onChange={(e) => {
                      setLedgerRowsPerPage(e.target.value);
                      setLedgerPage(1);
                    }}
                    className="p-2 text-xs border border-slate-200 rounded-lg bg-white outline-none"
                  >
                    <option value="10">10</option>
                    <option value="25">25</option>
                    <option value="50">50</option>
                    <option value="all">All</option>
                  </select>
                  <button
                    type="button"
                    onClick={() => setLedgerPage((prev) => Math.max(1, prev - 1))}
                    disabled={isShowAllRows || currentLedgerPage <= 1}
                    className="px-3 py-2 text-xs font-bold rounded-lg bg-slate-100 text-slate-700 hover:bg-slate-200 disabled:opacity-50"
                  >
                    Prev
                  </button>
                  <span className="text-xs font-semibold text-slate-600 min-w-[84px] text-center">
                    {isShowAllRows
                      ? "All pages"
                      : `Page ${currentLedgerPage} of ${totalLedgerPages}`}
                  </span>
                  <button
                    type="button"
                    onClick={() =>
                      setLedgerPage((prev) =>
                        Math.min(totalLedgerPages, prev + 1),
                      )
                    }
                    disabled={isShowAllRows || currentLedgerPage >= totalLedgerPages}
                    className="px-3 py-2 text-xs font-bold rounded-lg bg-slate-100 text-slate-700 hover:bg-slate-200 disabled:opacity-50"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {bulkEditOpen && (
          <div className="fixed inset-0 z-[80] bg-slate-900/40 flex items-center justify-center p-4">
            <div className="w-full max-w-xl bg-white rounded-2xl border border-slate-200 shadow-xl">
              <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
                <h3 className="text-sm font-bold text-slate-900">
                  Bulk Edit {selectedLedgerIds.length} Transaction(s)
                </h3>
                <button
                  onClick={() => setBulkEditOpen(false)}
                  className="text-xs font-bold text-slate-500 hover:text-slate-800"
                >
                  Close
                </button>
              </div>
              <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-3">
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wide">
                  Date
                  <input
                    type="date"
                    value={bulkEditForm.date}
                    onChange={(e) =>
                      setBulkEditForm((prev) => ({ ...prev, date: e.target.value }))
                    }
                    className="mt-1 w-full text-sm p-2 border border-slate-200 rounded-lg bg-white"
                  />
                </label>
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wide">
                  Type
                  <select
                    value={bulkEditForm.type}
                    onChange={(e) =>
                      setBulkEditForm((prev) => ({
                        ...prev,
                        type: e.target.value,
                        category: "",
                      }))
                    }
                    className="mt-1 w-full text-sm p-2 border border-slate-200 rounded-lg bg-white"
                  >
                    <option value="">No Change</option>
                    {Object.entries(transactionTypes).map(([k, c]) => (
                      <option key={k} value={k}>
                        {c.label}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wide">
                  Category
                  <select
                    value={bulkEditForm.category}
                    onChange={(e) =>
                      setBulkEditForm((prev) => ({
                        ...prev,
                        category: e.target.value,
                      }))
                    }
                    className="mt-1 w-full text-sm p-2 border border-slate-200 rounded-lg bg-white"
                  >
                    <option value="">No Change</option>
                    {bulkCategoryOptions.map((category) => (
                      <option key={category} value={category}>
                        {category}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wide">
                  Amount
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={bulkEditForm.amount}
                    onChange={(e) =>
                      setBulkEditForm((prev) => ({
                        ...prev,
                        amount: e.target.value,
                      }))
                    }
                    placeholder="No change when empty"
                    className="mt-1 w-full text-sm p-2 border border-slate-200 rounded-lg bg-white"
                  />
                </label>
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wide">
                  Payee / Vendor
                  <input
                    type="text"
                    value={bulkEditForm.payee}
                    onChange={(e) =>
                      setBulkEditForm((prev) => ({ ...prev, payee: e.target.value }))
                    }
                    placeholder="No change when empty"
                    className="mt-1 w-full text-sm p-2 border border-slate-200 rounded-lg bg-white"
                  />
                </label>
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wide md:col-span-2">
                  Notes
                  <input
                    type="text"
                    value={bulkEditForm.notes}
                    onChange={(e) =>
                      setBulkEditForm((prev) => ({ ...prev, notes: e.target.value }))
                    }
                    placeholder="No change when empty"
                    className="mt-1 w-full text-sm p-2 border border-slate-200 rounded-lg bg-white"
                  />
                </label>
              </div>
              <div className="px-5 py-4 border-t border-slate-200 flex justify-end gap-2">
                <button
                  onClick={() => setBulkEditOpen(false)}
                  className="px-3 py-2 text-xs font-bold rounded-lg bg-slate-100 text-slate-700 hover:bg-slate-200"
                >
                  Cancel
                </button>
                <button
                  onClick={applyBulkEdit}
                  className="px-3 py-2 text-xs font-bold rounded-lg bg-indigo-600 text-white hover:bg-indigo-700"
                >
                  Apply Changes
                </button>
              </div>
            </div>
          </div>
        )}

        {/* REPORTS */}
        {activeTab === "reports" && (
          <div className="space-y-6 animate-fade-in">
            <div className="bg-white p-2 rounded-xl shadow-sm border border-slate-200 flex flex-wrap gap-2 w-fit">
              {[
                {
                  id: "balance-sheet",
                  label: "Balance Sheet",
                  icon: <Landmark className="w-4 h-4" />,
                },
                {
                  id: "income-statement",
                  label: "Income Statement",
                  icon: <FileSpreadsheet className="w-4 h-4" />,
                },
                {
                  id: "cash-flow",
                  label: "Cash Flow",
                  icon: <TrendingUp className="w-4 h-4" />,
                },
                {
                  id: "equity",
                  label: "Shareholders' Equity",
                  icon: <LineChart className="w-4 h-4" />,
                },
              ].map((r) => (
                <button
                  key={r.id}
                  onClick={() => setReportType(r.id)}
                  className={`px-4 py-2.5 rounded-lg text-sm font-bold transition-all flex items-center gap-2 ${reportType === r.id ? "bg-indigo-50 text-indigo-700 shadow-sm border border-indigo-100" : "text-slate-500 hover:bg-slate-50 hover:text-slate-800"}`}
                >
                  {r.icon} {r.label}
                </button>
              ))}
            </div>

            <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-8 md:p-12 max-w-4xl mx-auto">
              <div className="text-center mb-10 border-b border-slate-200 pb-8">
                <h2 className="text-2xl font-black text-slate-900 uppercase tracking-[0.2em]">
                  {currentProject.name}
                </h2>
                <h3 className="text-lg text-slate-600 font-bold mt-2 uppercase tracking-widest">
                  {reportType === "balance-sheet" &&
                    "Statement of Financial Position"}
                  {reportType === "income-statement" &&
                    "Statement of Profit or Loss"}
                  {reportType === "cash-flow" && "Statement of Cash Flows"}
                  {reportType === "equity" &&
                    "Statement of Shareholders' Equity"}
                </h3>
                <p className="text-sm text-slate-500 mt-3 font-medium">
                  As of{" "}
                  {new Date().toLocaleDateString("en-US", {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })}
                </p>
                <p className="text-[10px] text-slate-400 mt-1 uppercase tracking-widest font-bold">
                  All amounts stated in NGN
                </p>
              </div>

              {/* RENDER REPORT LOGIC (Cleaned up from File 2) */}
              <div className="space-y-8 text-sm max-w-2xl mx-auto">
                {reportType === "balance-sheet" && (
                  <>
                    <div>
                      <h4 className="font-bold text-lg text-slate-900 border-b-2 border-slate-800 pb-2 mb-4">
                        Assets
                      </h4>
                      <div className="flex justify-between py-2 pl-4 text-slate-700">
                        <span className="font-medium">
                          Cash and Cash Equivalents
                        </span>
                        <span className="font-mono">
                          {formatAcc(financialData.totalAssets)}
                        </span>
                      </div>
                      <div className="flex justify-between py-3 font-bold text-slate-900 mt-2 border-t border-slate-300">
                        <span>Total Assets</span>
                        <span className="border-b-[3px] border-slate-800 border-double font-mono">
                          {formatAcc(financialData.totalAssets)}
                        </span>
                      </div>
                    </div>
                    <div>
                      <h4 className="font-bold text-lg text-slate-900 border-b-2 border-slate-800 pb-2 mb-4">
                        Liabilities
                      </h4>
                      {financialData.liabilityBreakdown.map(([c, a]) => (
                        <div
                          key={c}
                          className="flex justify-between py-2 pl-4 text-slate-700"
                        >
                          <span className="font-medium">{c}</span>
                          <span className="font-mono">{formatAcc(a)}</span>
                        </div>
                      ))}
                      {!financialData.liabilityBreakdown.length && (
                        <div className="py-2 pl-4 text-slate-400 italic">
                          No liabilities recorded
                        </div>
                      )}
                      <div className="flex justify-between py-3 font-bold text-slate-900 mt-2 border-t border-slate-300">
                        <span>Total Liabilities</span>
                        <span className="font-mono">
                          {formatAcc(financialData.liabilities)}
                        </span>
                      </div>
                    </div>
                    <div>
                      <h4 className="font-bold text-lg text-slate-900 border-b-2 border-slate-800 pb-2 mb-4">
                        Shareholders' Equity
                      </h4>
                      {financialData.equityBreakdown.map(([c, a]) => (
                        <div
                          key={c}
                          className="flex justify-between py-2 pl-4 text-slate-700"
                        >
                          <span className="font-medium">{c}</span>
                          <span className="font-mono">{formatAcc(a)}</span>
                        </div>
                      ))}
                      <div className="flex justify-between py-2 pl-4 text-slate-700">
                        <span className="font-medium">
                          Retained Earnings (Net Income)
                        </span>
                        <span className="font-mono">
                          {formatAcc(financialData.netIncome)}
                        </span>
                      </div>
                      <div className="flex justify-between py-3 font-bold text-slate-900 mt-2 border-t border-slate-300">
                        <span>Total Equity</span>
                        <span className="font-mono">
                          {formatAcc(
                            financialData.equity + financialData.netIncome,
                          )}
                        </span>
                      </div>
                    </div>
                    <div className="flex justify-between py-4 font-bold text-slate-900 mt-8 border-t-[3px] border-slate-800 bg-slate-50 px-4 rounded-b-xl text-base">
                      <span>Total Liabilities & Equity</span>
                      <span className="border-b-[4px] border-slate-800 border-double font-mono">
                        {formatAcc(financialData.totalLiabilitiesAndEquity)}
                      </span>
                    </div>
                  </>
                )}
                {reportType === "income-statement" && (
                  <>
                    <div>
                      <h4 className="font-bold text-lg text-slate-900 border-b-2 border-slate-800 pb-2 mb-4">
                        Revenues
                      </h4>
                      {financialData.revenueBreakdown.map(([c, a]) => (
                        <div
                          key={c}
                          className="flex justify-between py-2 pl-4 text-slate-700"
                        >
                          <span className="font-medium">{c}</span>
                          <span className="font-mono">{formatAcc(a)}</span>
                        </div>
                      ))}
                      {!financialData.revenueBreakdown.length && (
                        <div className="py-2 pl-4 text-slate-400 italic">
                          No revenue recorded
                        </div>
                      )}
                      <div className="flex justify-between py-3 font-bold text-slate-900 mt-2 border-t border-slate-300">
                        <span>Total Revenues</span>
                        <span className="font-mono">
                          {formatAcc(financialData.revenue)}
                        </span>
                      </div>
                    </div>
                    <div>
                      <h4 className="font-bold text-lg text-slate-900 border-b-2 border-slate-800 pb-2 mb-4">
                        Operating Expenses
                      </h4>
                      {financialData.expenseBreakdown.map(([c, a]) => (
                        <div
                          key={c}
                          className="flex justify-between py-2 pl-4 text-slate-700"
                        >
                          <span className="font-medium">{c}</span>
                          <span className="font-mono">{formatAcc(a)}</span>
                        </div>
                      ))}
                      {!financialData.expenseBreakdown.length && (
                        <div className="py-2 pl-4 text-slate-400 italic">
                          No expenses recorded
                        </div>
                      )}
                      <div className="flex justify-between py-3 font-bold text-slate-900 mt-2 border-t border-slate-300">
                        <span>Total Operating Expenses</span>
                        <span className="font-mono">
                          {formatAcc(financialData.expenses)}
                        </span>
                      </div>
                    </div>
                    <div className="flex justify-between py-4 font-bold text-slate-900 mt-8 border-t-[3px] border-slate-800 bg-slate-50 px-4 rounded-b-xl text-base">
                      <span>Net Income (Loss)</span>
                      <span className="border-b-[4px] border-slate-800 border-double font-mono">
                        {formatAcc(financialData.netIncome)}
                      </span>
                    </div>
                  </>
                )}
                {reportType === "cash-flow" && (
                  <>
                    <div>
                      <h4 className="font-bold text-lg text-slate-900 border-b-2 border-slate-800 pb-2 mb-4">
                        Cash Flows from Operating Activities
                      </h4>
                      <div className="flex justify-between py-2 pl-4 text-slate-700">
                        <span className="font-medium">
                          Cash received from customers/ops
                        </span>
                        <span className="font-mono">
                          {formatAcc(financialData.revenue)}
                        </span>
                      </div>
                      <div className="flex justify-between py-2 pl-4 text-slate-700">
                        <span className="font-medium">
                          Cash paid to suppliers/contractors
                        </span>
                        <span className="font-mono">
                          {formatAcc(-financialData.expenses)}
                        </span>
                      </div>
                      <div className="flex justify-between py-3 font-bold text-slate-900 mt-2 border-t border-slate-300">
                        <span>Net Cash from Operating Activities</span>
                        <span className="font-mono">
                          {formatAcc(financialData.cashFromOperations)}
                        </span>
                      </div>
                    </div>
                    <div>
                      <h4 className="font-bold text-lg text-slate-900 border-b-2 border-slate-800 pb-2 mb-4">
                        Cash Flows from Financing Activities
                      </h4>
                      <div className="flex justify-between py-2 pl-4 text-slate-700">
                        <span className="font-medium">
                          Proceeds from Loans & Liabilities
                        </span>
                        <span className="font-mono">
                          {formatAcc(financialData.liabilities)}
                        </span>
                      </div>
                      <div className="flex justify-between py-2 pl-4 text-slate-700">
                        <span className="font-medium">
                          Proceeds from Equity Contributions
                        </span>
                        <span className="font-mono">
                          {formatAcc(financialData.equity)}
                        </span>
                      </div>
                      <div className="flex justify-between py-3 font-bold text-slate-900 mt-2 border-t border-slate-300">
                        <span>Net Cash from Financing Activities</span>
                        <span className="font-mono">
                          {formatAcc(financialData.cashFromFinancing)}
                        </span>
                      </div>
                    </div>
                    <div className="flex justify-between py-4 font-bold text-slate-900 mt-8 border-t-[3px] border-slate-800 bg-slate-50 px-4 rounded-b-xl text-base">
                      <span>Net Increase in Cash</span>
                      <span className="border-b-[4px] border-slate-800 border-double font-mono">
                        {formatAcc(
                          financialData.cashFromOperations +
                            financialData.cashFromFinancing,
                        )}
                      </span>
                    </div>
                  </>
                )}
                {reportType === "equity" && (
                  <table className="w-full text-left">
                    <thead className="border-b-[3px] border-slate-800">
                      <tr>
                        <th className="py-3 font-bold text-slate-900">
                          Description
                        </th>
                        <th className="py-3 font-bold text-slate-900 text-right">
                          Contributed
                        </th>
                        <th className="py-3 font-bold text-slate-900 text-right">
                          Retained
                        </th>
                        <th className="py-3 font-bold text-slate-900 text-right">
                          Total Equity
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200">
                      <tr>
                        <td className="py-4 text-slate-700 font-medium">
                          Beginning Balance
                        </td>
                        <td className="py-4 font-mono text-slate-900 text-right">
                          {formatAcc(0)}
                        </td>
                        <td className="py-4 font-mono text-slate-900 text-right">
                          {formatAcc(0)}
                        </td>
                        <td className="py-4 font-bold font-mono text-slate-900 text-right">
                          {formatAcc(0)}
                        </td>
                      </tr>
                      <tr>
                        <td className="py-4 text-slate-700 font-medium">
                          Capital Contributions
                        </td>
                        <td className="py-4 font-mono text-slate-900 text-right">
                          {formatAcc(financialData.equity)}
                        </td>
                        <td className="py-4 font-mono text-slate-900 text-right">
                          {formatAcc(0)}
                        </td>
                        <td className="py-4 font-bold font-mono text-slate-900 text-right">
                          {formatAcc(financialData.equity)}
                        </td>
                      </tr>
                      <tr>
                        <td className="py-4 text-slate-700 font-medium">
                          Net Income
                        </td>
                        <td className="py-4 font-mono text-slate-900 text-right">
                          {formatAcc(0)}
                        </td>
                        <td className="py-4 font-mono text-slate-900 text-right">
                          {formatAcc(financialData.netIncome)}
                        </td>
                        <td className="py-4 font-bold font-mono text-slate-900 text-right">
                          {formatAcc(financialData.netIncome)}
                        </td>
                      </tr>
                      <tr className="border-t-[3px] border-slate-800 bg-slate-50">
                        <td className="py-4 font-bold text-slate-900 px-2 rounded-bl-xl">
                          Ending Balance
                        </td>
                        <td className="py-4 font-bold font-mono text-slate-900 text-right">
                          {formatAcc(financialData.equity)}
                        </td>
                        <td className="py-4 font-bold font-mono text-slate-900 text-right">
                          {formatAcc(financialData.netIncome)}
                        </td>
                        <td className="py-4 font-bold font-mono text-slate-900 text-right px-2 rounded-br-xl">
                          <span className="border-b-[4px] border-slate-800 border-double">
                            {formatAcc(
                              financialData.equity + financialData.netIncome,
                            )}
                          </span>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ROI CALCULATOR */}
        {activeTab === "roi" && (
          <React.Suspense
            fallback={
              <div className="text-center py-12 text-slate-400">
                Loading calculator...
              </div>
            }
          >
            <ROICalculator />
          </React.Suspense>
        )}
      </div>
      <style>{`
        .animate-fade-in { animation: fadeIn 0.3s ease-in-out; }
        @keyframes fadeIn { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>
    </div>
  );
}

// // src/App.jsx
// // ─────────────────────────────────────────────────────────
// // Root component. Handles auth, routing between views,
// // and wires together all hooks and child components.
// // ─────────────────────────────────────────────────────────

// import React, { useState, useMemo } from "react";
// import {
//   Wallet,
//   ArrowLeft,
//   BarChart3,
//   Calculator,
//   List,
//   FileText,
//   ArrowDownCircle,
//   ArrowUpCircle,
//   PieChart,
//   Download,
// } from "lucide-react";

// import { useAuth } from "./hooks/useAuth";
// import { useProjects } from "./hooks/useProjects";
// import { useTransactions } from "./hooks/useTransactions";

// import ProjectWorkspace from "./components/ProjectWorkspace";
// import TransactionForm from "./components/TransactionForm";
// import KPICards from "./components/KPICards";
// import AIInsights from "./components/AIInsights";

// import {
//   DEFAULT_TRANSACTION_TYPES,
//   formatCurrency,
//   formatAccounting,
//   SORT_OPTIONS,
// } from "./lib/constants";

// // Lazy-load heavy components
// const ROICalculator = React.lazy(() => import("./components/ROICalculator"));

// // ─────────────────────────────────────────────────────────
// export default function App() {
//   const { user, loading: authLoading } = useAuth();
//   const { projects, create: createProject } = useProjects(user?.uid);

//   const [currentProject, setCurrentProject] = useState(null);
//   const [activeTab, setActiveTab] = useState("dashboard");
//   const [editingTx, setEditingTx] = useState(null);
//   const [filterType, setFilterType] = useState("all");
//   const [filterCategory, setFilterCategory] = useState("all");
//   const [sortBy, setSortBy] = useState("date-desc");
//   const [reportType, setReportType] = useState("balance-sheet");

//   const { transactions, add, update, remove } = useTransactions(
//     user?.uid,
//     currentProject?.id,
//   );

//   // ── Derived: summary totals ───────────────────────────
//   const { totalInflows, totalOutflows, balance, expensesByCategory } =
//     useMemo(() => {
//       let inflows = 0,
//         outflows = 0;
//       const byCategory = {};

//       transactions.forEach((t) => {
//         const amt = parseFloat(t.amount);
//         const cfg = DEFAULT_TRANSACTION_TYPES[t.type] || { type: "outflow" };
//         if (cfg.type === "inflow") {
//           inflows += amt;
//         } else {
//           outflows += amt;
//           byCategory[t.category] = (byCategory[t.category] || 0) + amt;
//         }
//       });

//       return {
//         totalInflows: inflows,
//         totalOutflows: outflows,
//         balance: inflows - outflows,
//         expensesByCategory: Object.entries(byCategory).sort(
//           (a, b) => b[1] - a[1],
//         ),
//       };
//     }, [transactions]);

//   // ── Derived: filtered/sorted ledger ──────────────────
//   const { filtered, filterCats } = useMemo(() => {
//     let result = [...transactions];

//     if (filterType !== "all") {
//       result = result.filter((t) =>
//         filterType === "inflow" || filterType === "outflow"
//           ? (DEFAULT_TRANSACTION_TYPES[t.type] || { type: "outflow" }).type ===
//             filterType
//           : t.type === filterType,
//       );
//     }

//     const cats = new Set(result.map((t) => t.category));
//     if (filterCategory !== "all")
//       result = result.filter((t) => t.category === filterCategory);

//     result.sort((a, b) => {
//       if (sortBy === "date-desc") return new Date(b.date) - new Date(a.date);
//       if (sortBy === "date-asc") return new Date(a.date) - new Date(b.date);
//       if (sortBy === "amount-desc") return b.amount - a.amount;
//       if (sortBy === "amount-asc") return a.amount - b.amount;
//       return 0;
//     });

//     return { filtered: result, filterCats: Array.from(cats).sort() };
//   }, [transactions, filterType, filterCategory, sortBy]);

//   // ── CSV export ────────────────────────────────────────
//   const exportCSV = () => {
//     const data = filtered.length ? filtered : transactions;
//     if (!data.length) return;

//     const rows = data.map((t) => {
//       const isInflow =
//         (DEFAULT_TRANSACTION_TYPES[t.type] || { type: "outflow" }).type ===
//         "inflow";
//       return [
//         t.date,
//         `"${(DEFAULT_TRANSACTION_TYPES[t.type] || {}).label || t.type}"`,
//         `"${t.category}"`,
//         `"${t.payee.replace(/"/g, '""')}"`,
//         `"${(t.notes || "").replace(/"/g, '""')}"`,
//         isInflow ? t.amount : -t.amount,
//       ].join(",");
//     });

//     const csv = ["Date,Type,Category,Payee,Notes,Amount", ...rows].join("\n");
//     const link = document.createElement("a");
//     link.href = URL.createObjectURL(
//       new Blob([csv], { type: "text/csv;charset=utf-8;" }),
//     );
//     link.download = `${currentProject.name.replace(/\s+/g, "_")}_Ledger.csv`;
//     document.body.appendChild(link);
//     link.click();
//     document.body.removeChild(link);
//   };

//   // ── Loading / auth guard ──────────────────────────────
//   if (authLoading) {
//     return (
//       <div className="min-h-screen flex items-center justify-center bg-slate-50 text-slate-500 font-medium">
//         Authenticating...
//       </div>
//     );
//   }

//   // ── Project selection screen ──────────────────────────
//   if (!currentProject) {
//     return (
//       <ProjectWorkspace
//         projects={projects}
//         onCreate={createProject}
//         onSelect={setCurrentProject}
//       />
//     );
//   }

//   // ── Main app ──────────────────────────────────────────
//   const tabs = [
//     { id: "dashboard", label: "Dashboard" },
//     { id: "ledger", label: "Detailed Ledger" },
//     {
//       id: "reports",
//       label: "Reports",
//       icon: <BarChart3 className="w-4 h-4 mr-1.5 inline" />,
//     },
//     {
//       id: "roi",
//       label: "ROI Calculator",
//       icon: <Calculator className="w-4 h-4 mr-1.5 inline" />,
//     },
//   ];

//   return (
//     <div className="min-h-screen bg-slate-50 text-slate-800 font-sans p-4 md:p-6 lg:p-8">
//       <div className="max-w-7xl mx-auto space-y-6">
//         {/* Header */}
//         <header className="bg-white p-5 rounded-2xl shadow-sm border border-slate-200 flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
//           <div className="flex flex-col items-start gap-3">
//             <button
//               onClick={() => {
//                 setCurrentProject(null);
//                 setActiveTab("dashboard");
//               }}
//               className="flex items-center gap-1.5 text-xs font-bold text-slate-500 hover:text-indigo-600 bg-slate-100 hover:bg-indigo-50 px-3 py-1.5 rounded-full transition-colors"
//             >
//               <ArrowLeft className="w-3.5 h-3.5" /> All Projects
//             </button>
//             <div className="flex items-center gap-3">
//               <div className="p-2 bg-indigo-50 rounded-xl">
//                 <Wallet className="w-6 h-6 text-indigo-600" />
//               </div>
//               <div>
//                 <h1 className="text-xl font-bold text-slate-900">
//                   {currentProject.name} Tracker
//                 </h1>
//                 <p className="text-slate-500 text-xs mt-0.5 font-medium">
//                   Manage disbursements, expenses, and cash flow
//                 </p>
//               </div>
//             </div>
//           </div>

//           <div className="flex bg-slate-100/80 p-1.5 rounded-xl overflow-x-auto w-full lg:w-auto flex-nowrap shrink-0">
//             {tabs.map((tab) => (
//               <button
//                 key={tab.id}
//                 onClick={() => setActiveTab(tab.id)}
//                 className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all whitespace-nowrap ${
//                   activeTab === tab.id
//                     ? "bg-white text-indigo-700 shadow-sm border border-slate-200/50"
//                     : "text-slate-500 hover:text-slate-800 hover:bg-slate-200/50"
//                 }`}
//               >
//                 {tab.icon}
//                 {tab.label}
//               </button>
//             ))}
//           </div>
//         </header>

//         {/* ── DASHBOARD ── */}
//         {activeTab === "dashboard" && (
//           <div className="space-y-6">
//             <KPICards
//               totalInflows={totalInflows}
//               totalOutflows={totalOutflows}
//               balance={balance}
//             />

//             <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
//               {/* Form column */}
//               <div className="lg:col-span-1">
//                 <TransactionForm
//                   onAdd={add}
//                   onUpdate={update}
//                   editingTx={editingTx}
//                   onCancelEdit={() => setEditingTx(null)}
//                 />
//               </div>

//               {/* Insights + Distribution column */}
//               <div className="lg:col-span-2 space-y-6">
//                 <AIInsights
//                   totalInflows={totalInflows}
//                   totalOutflows={totalOutflows}
//                   balance={balance}
//                   expensesByCategory={expensesByCategory}
//                 />

//                 {/* Outflow distribution */}
//                 <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100">
//                   <h2 className="text-base font-bold text-slate-800 mb-6 flex items-center gap-2">
//                     <PieChart className="w-5 h-5 text-indigo-500" /> Outflow
//                     Distribution
//                   </h2>
//                   {!expensesByCategory.length ? (
//                     <div className="text-center py-8 text-slate-400 text-sm bg-slate-50 rounded-xl border border-dashed border-slate-200">
//                       No outflows recorded yet.
//                     </div>
//                   ) : (
//                     <div className="space-y-5">
//                       {expensesByCategory.map(([cat, amt]) => {
//                         const pct =
//                           totalOutflows > 0
//                             ? ((amt / totalOutflows) * 100).toFixed(1)
//                             : 0;
//                         return (
//                           <div key={cat} className="space-y-1.5">
//                             <div className="flex justify-between text-sm">
//                               <span className="font-bold text-slate-700">
//                                 {cat}
//                               </span>
//                               <span className="text-slate-500 font-medium">
//                                 {formatCurrency(amt)}{" "}
//                                 <span className="text-xs text-slate-400">
//                                   ({pct}%)
//                                 </span>
//                               </span>
//                             </div>
//                             <div className="w-full bg-slate-100 rounded-full h-2">
//                               <div
//                                 className="bg-indigo-500 h-2 rounded-full"
//                                 style={{ width: `${pct}%` }}
//                               />
//                             </div>
//                           </div>
//                         );
//                       })}
//                     </div>
//                   )}
//                 </div>

//                 {/* Recent activity */}
//                 <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100">
//                   <div className="flex justify-between items-center mb-5">
//                     <h2 className="text-base font-bold text-slate-800 flex items-center gap-2">
//                       <List className="w-5 h-5 text-indigo-500" /> Recent
//                       Activity
//                     </h2>
//                     <button
//                       onClick={() => setActiveTab("ledger")}
//                       className="text-xs text-indigo-600 hover:text-indigo-800 font-bold"
//                     >
//                       View All →
//                     </button>
//                   </div>

//                   <div className="divide-y divide-slate-100">
//                     {transactions.slice(0, 4).map((t) => {
//                       const isInflow =
//                         (
//                           DEFAULT_TRANSACTION_TYPES[t.type] || {
//                             type: "outflow",
//                           }
//                         ).type === "inflow";
//                       return (
//                         <div
//                           key={t.id}
//                           className="py-3.5 flex justify-between items-center group"
//                         >
//                           <div className="flex items-center gap-3.5">
//                             <div
//                               className={`p-2 rounded-xl border ${
//                                 isInflow
//                                   ? "bg-indigo-50 text-indigo-600 border-indigo-100"
//                                   : "bg-slate-50 text-slate-500 border-slate-200"
//                               }`}
//                             >
//                               {isInflow ? (
//                                 <ArrowDownCircle className="w-4 h-4" />
//                               ) : (
//                                 <ArrowUpCircle className="w-4 h-4" />
//                               )}
//                             </div>
//                             <div>
//                               <p className="text-sm font-bold text-slate-800">
//                                 {t.payee}
//                               </p>
//                               <p className="text-xs text-slate-500">
//                                 {t.category} · {t.date}
//                               </p>
//                             </div>
//                           </div>
//                           <div className="flex items-center gap-3">
//                             <button
//                               onClick={() => {
//                                 setEditingTx(t);
//                                 setActiveTab("dashboard");
//                               }}
//                               className="opacity-0 group-hover:opacity-100 text-xs text-indigo-500 font-bold"
//                             >
//                               Edit
//                             </button>
//                             <span
//                               className={`text-sm font-bold font-mono ${isInflow ? "text-indigo-600" : "text-slate-800"}`}
//                             >
//                               {isInflow ? "+" : "-"}
//                               {formatCurrency(t.amount)}
//                             </span>
//                           </div>
//                         </div>
//                       );
//                     })}
//                     {!transactions.length && (
//                       <div className="text-center py-6 text-slate-400 text-sm bg-slate-50 rounded-xl border border-dashed border-slate-200">
//                         No transactions yet.
//                       </div>
//                     )}
//                   </div>
//                 </div>
//               </div>
//             </div>
//           </div>
//         )}

//         {/* ── LEDGER ── */}
//         {activeTab === "ledger" && (
//           <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
//             <div className="p-6 border-b border-slate-100 flex justify-between items-center">
//               <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
//                 <FileText className="w-5 h-5 text-indigo-500" /> Complete
//                 Transaction Ledger
//               </h2>
//               <button
//                 onClick={exportCSV}
//                 disabled={!transactions.length}
//                 className="flex items-center gap-2 px-4 py-2 bg-white text-slate-700 border border-slate-200 rounded-lg text-sm font-bold hover:bg-slate-50 disabled:opacity-50"
//               >
//                 <Download className="w-4 h-4" /> Export to Excel
//               </button>
//             </div>

//             {/* Filters */}
//             <div className="bg-slate-50 p-5 border-b flex flex-wrap gap-4 items-center justify-between">
//               <div className="flex flex-wrap gap-4">
//                 <div className="flex items-center gap-2">
//                   <span className="text-xs font-bold text-slate-500 uppercase">
//                     Type:
//                   </span>
//                   <select
//                     value={filterType}
//                     onChange={(e) => {
//                       setFilterType(e.target.value);
//                       setFilterCategory("all");
//                     }}
//                     className="p-2 text-sm border border-slate-200 rounded-lg bg-white outline-none"
//                   >
//                     <option value="all">All Types</option>
//                     <option value="inflow">All Inflows (+)</option>
//                     <option value="outflow">All Outflows (-)</option>
//                     {Object.entries(DEFAULT_TRANSACTION_TYPES).map(([k, c]) => (
//                       <option key={k} value={k}>
//                         {c.label}
//                       </option>
//                     ))}
//                   </select>
//                 </div>
//                 <div className="flex items-center gap-2">
//                   <span className="text-xs font-bold text-slate-500 uppercase">
//                     Category:
//                   </span>
//                   <select
//                     value={filterCategory}
//                     onChange={(e) => setFilterCategory(e.target.value)}
//                     className="p-2 text-sm border border-slate-200 rounded-lg bg-white outline-none"
//                   >
//                     <option value="all">All Categories</option>
//                     {filterCats.map((c) => (
//                       <option key={c} value={c}>
//                         {c}
//                       </option>
//                     ))}
//                   </select>
//                 </div>
//               </div>
//               <div className="flex items-center gap-2">
//                 <span className="text-xs font-bold text-slate-500 uppercase">
//                   Sort:
//                 </span>
//                 <select
//                   value={sortBy}
//                   onChange={(e) => setSortBy(e.target.value)}
//                   className="p-2 text-sm border border-slate-200 rounded-lg bg-white outline-none"
//                 >
//                   {SORT_OPTIONS.map((o) => (
//                     <option key={o.value} value={o.value}>
//                       {o.label}
//                     </option>
//                   ))}
//                 </select>
//               </div>
//             </div>

//             {/* Table */}
//             <div className="overflow-x-auto">
//               <table className="w-full text-left text-sm">
//                 <thead className="bg-white text-slate-500 font-bold text-xs uppercase tracking-wider border-b border-slate-200">
//                   <tr>
//                     {[
//                       "Date",
//                       "Type",
//                       "Category",
//                       "Payee / Vendor",
//                       "Notes",
//                       "Amount",
//                       "Action",
//                     ].map((h) => (
//                       <th
//                         key={h}
//                         className={`px-6 py-4 ${h === "Amount" || h === "Action" ? "text-right" : ""}`}
//                       >
//                         {h}
//                       </th>
//                     ))}
//                   </tr>
//                 </thead>
//                 <tbody className="divide-y divide-slate-100">
//                   {filtered.map((t) => {
//                     const isInflow =
//                       (DEFAULT_TRANSACTION_TYPES[t.type] || { type: "outflow" })
//                         .type === "inflow";
//                     return (
//                       <tr key={t.id} className="hover:bg-slate-50">
//                         <td className="px-6 py-4 text-slate-600 font-medium whitespace-nowrap">
//                           {t.date}
//                         </td>
//                         <td className="px-6 py-4">
//                           <span
//                             className={`inline-flex px-2 py-1 rounded-md text-[11px] font-bold uppercase ${
//                               isInflow
//                                 ? "bg-indigo-50 text-indigo-700"
//                                 : "bg-slate-100 text-slate-600"
//                             }`}
//                           >
//                             {(DEFAULT_TRANSACTION_TYPES[t.type] || {}).label ||
//                               t.type}
//                           </span>
//                         </td>
//                         <td className="px-6 py-4 text-slate-800">
//                           {t.category}
//                         </td>
//                         <td className="px-6 py-4 font-bold text-slate-900">
//                           {t.payee}
//                         </td>
//                         <td className="px-6 py-4 text-slate-500 truncate max-w-[200px]">
//                           {t.notes}
//                         </td>
//                         <td
//                           className={`px-6 py-4 text-right font-bold font-mono whitespace-nowrap ${isInflow ? "text-indigo-600" : "text-slate-900"}`}
//                         >
//                           {isInflow ? "+" : "-"}
//                           {formatCurrency(t.amount)}
//                         </td>
//                         <td className="px-6 py-4 text-right whitespace-nowrap">
//                           <button
//                             onClick={() => {
//                               setEditingTx(t);
//                               setActiveTab("dashboard");
//                             }}
//                             className="text-indigo-600 hover:text-indigo-800 text-xs font-bold mr-4"
//                           >
//                             Edit
//                           </button>
//                           <button
//                             onClick={() => remove(t.id)}
//                             className="text-rose-500 hover:text-rose-700 text-xs font-bold"
//                           >
//                             Del
//                           </button>
//                         </td>
//                       </tr>
//                     );
//                   })}
//                   {!filtered.length && (
//                     <tr>
//                       <td
//                         colSpan={7}
//                         className="px-6 py-12 text-center text-slate-500 bg-slate-50"
//                       >
//                         No transactions match the criteria.
//                       </td>
//                     </tr>
//                   )}
//                 </tbody>
//               </table>
//             </div>
//           </div>
//         )}

//         {/* ── REPORTS ── (kept inline — mostly display logic) */}
//         {activeTab === "reports" && (
//           <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-8 text-center text-slate-400">
//             Reports tab — wire in your Reports component here following the same
//             pattern.
//             <br />
//             <span className="text-xs">
//               See the full code in the original App.jsx reports section.
//             </span>
//           </div>
//         )}

//         {/* ── ROI CALCULATOR ── */}
//         {activeTab === "roi" && (
//           <React.Suspense
//             fallback={
//               <div className="text-center py-12 text-slate-400">
//                 Loading calculator...
//               </div>
//             }
//           >
//             <ROICalculator />
//           </React.Suspense>
//         )}
//       </div>

//       <style>{`
//         .animate-fade-in { animation: fadeIn 0.3s ease-in-out; }
//         @keyframes fadeIn { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: translateY(0); } }
//       `}</style>
//     </div>
//   );
// }





